/**
 * FocusRail — the "how far out am I?" instrument in the top bar. The workspace
 * switcher beside it answers "as whom"; this answers "how far out".
 *
 *   ‹ │ Claim │ Document │ Invention │ [My inventions ●] │ ›
 *   You're at: My inventions · Sharing · 1 step waiting
 *
 * One named notch per level in the viewer's window, in the four states the
 * design names: the current notch filled, a reachable notch outlined and
 * clickable, a notch whose own banner has a step waiting outlined with a small
 * dot, and a locked notch grey with a lock glyph and its reason on hover —
 * visible and named, never hidden and never a dead control. Two chevrons zoom
 * in and out, and the same two moves are on the `[` and `]` keys.
 *
 * Under the notches runs the spring-eased bar the codebase already uses for
 * the project tabs: it slides to the current notch with a slight overshoot, so
 * a zoom reads as movement along one ladder rather than a page swap. The rail
 * carries no colour semantics — purple is AI explanation on these same
 * screens, and amber is reserved for opportunity geometry.
 *
 * Renders nothing until a page says where the viewer is: a rail is a statement
 * about zoom, and only a graduated screen makes it.
 */
import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useSpine } from './useSpine'
import { useZoomShortcuts } from './useZoomShortcuts'
import {
  SPINE_DEFAULT_LINK,
  SPINE_LEVEL_LABEL,
  SPINE_LEVEL_QUESTION,
  SPINE_LOCK_REASON,
  stepsWaitingLabel,
} from './spineLevels'
import type { SpineLevel } from './spineLevels'
import type { SpineTail } from './SpineContext'

/** Does this browser say the reader wants less movement? Guarded because jsdom
 * has no matchMedia unless a test supplies one. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** The overshoot the project tabs already use — one motion, one feel. */
const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)'

const NOTCH = 'flex items-center gap-1.5 whitespace-nowrap rounded-full px-[11px] py-[5px]'
const CHEVRON = 'flex items-center justify-center rounded-full px-2 py-[5px] text-[13px] leading-none'
const SEP = 'h-3.5 w-px shrink-0 bg-slate-200'

function Chevron({ glyph, title, to }: { glyph: string; title: string; to?: string }) {
  if (!to) {
    return (
      <span className={`${CHEVRON} cursor-not-allowed text-slate-400 opacity-50`} title={title} aria-label={title} role="button" aria-disabled="true">
        {glyph}
      </span>
    )
  }
  return (
    <Link to={to} className={`${CHEVRON} text-iflow-700 hover:bg-iflow-50`} title={title} aria-label={title}>
      {glyph}
    </Link>
  )
}

/** One "You're at" tail piece: plain text, or a bolded word with a plain suffix. */
function Tail({ part }: { part: SpineTail }) {
  if (typeof part === 'string') return <>{part}</>
  return (
    <>
      <b className="font-semibold text-iflow-800">{part.b}</b>
      {part.text ? ` ${part.text}` : null}
    </>
  )
}

export default function FocusRail() {
  const spec = useSpine()
  useZoomShortcuts(spec)

  const groupRef = useRef<HTMLDivElement | null>(null)
  const currentRef = useRef<HTMLElement | null>(null)
  const [bar, setBar] = useState<{ left: number; width: number } | null>(null)
  // REDUCED MOTION MEANS NO MOTION (review round 8, item 2). The indicator's
  // 400ms slide is the one animated thing on this rail, and a capture taken
  // mid-slide shows the bar between two notches — which is how the round-8 S4b
  // screenshot caught it at x[690,724] instead of settled under the active
  // pill. A reader who has asked their system for reduced motion gets the bar
  // placed rather than slid, and so does anything that emulates that
  // preference: the same setting makes the rail deterministic to photograph.
  const [reduceMotion, setReduceMotion] = useState(() => prefersReducedMotion())
  useLayoutEffect(() => {
    const mq = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null
    if (!mq) return
    const onChange = () => setReduceMotion(mq.matches)
    onChange()
    // Safari < 14 has addListener only; both are guarded because jsdom supplies
    // whichever the test's stub happens to define.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }
    if (typeof mq.addListener === 'function') {
      mq.addListener(onChange)
      return () => mq.removeListener(onChange)
    }
    return
  }, [])

  const measure = useCallback(() => {
    const group = groupRef.current
    const notch = currentRef.current
    if (!group || !notch) {
      setBar(null)
      return
    }
    const g = group.getBoundingClientRect()
    const n = notch.getBoundingClientRect()
    if (n.width === 0) {
      setBar(null)
      return
    }
    setBar({ left: n.left - g.left + 9, width: Math.max(n.width - 18, 0) })
  }, [])

  useLayoutEffect(() => {
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure, spec])

  // No spec, or a screen that names no levels (figure C4, the organisation's
  // own preferences): there is no zoom to state, so there is no rail.
  if (!spec || spec.levels.length === 0) return null

  const waiting = stepsWaitingLabel(spec.stepsWaiting)
  const tail: SpineTail[] = [...spec.youreAt, ...(waiting ? [waiting] : [])]
  const pending = new Set<SpineLevel>([
    ...(spec.pendingLevels ?? []),
    ...(spec.stepsWaiting ? [spec.level] : []),
  ])
  // The figures put the keyboard move in the chevron's own tooltip, so the
  // shortcut is discoverable from the control it drives.
  const zoomInTitle = `${spec.zoomIn?.title ?? 'Zoom in — this is the closest view'} ([)`
  const zoomOutTitle = `${spec.zoomOut?.title ?? 'Zoom out — this is the widest view'} (])`

  return (
    <div className="hidden flex-col items-start gap-1 md:flex" data-testid="focus-rail">
      <nav
        aria-label="Focus rail"
        ref={groupRef}
        className="relative flex items-center rounded-full border border-slate-200 bg-white p-[3px] text-[11.5px] font-semibold text-slate-500"
      >
        <Chevron glyph="‹" title={zoomInTitle} to={spec.zoomIn?.to} />
        <span className={SEP} aria-hidden />
        {spec.levels.map((lvl, i) => {
          const on = lvl === spec.level
          const lock = spec.locked?.[lvl]
          const to = lock ? undefined : spec.links?.[lvl] ?? SPINE_DEFAULT_LINK[lvl]
          const title = lock ? `${SPINE_LEVEL_LABEL[lvl]} — ${SPINE_LOCK_REASON[lock]}` : SPINE_LEVEL_QUESTION[lvl]
          const cls = on
            ? `${NOTCH} bg-iflow-900 text-white`
            : lock
              ? `${NOTCH} cursor-not-allowed text-slate-400 opacity-60`
              : `${NOTCH} text-slate-500 hover:bg-iflow-50 hover:text-iflow-800`
          const inner = (
            <>
              {SPINE_LEVEL_LABEL[lvl]}
              {lock ? <Lock size={10} aria-hidden className="shrink-0" /> : null}
              {!lock && pending.has(lvl) ? (
                <span
                  className="inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-current"
                  title="A step is waiting at this level"
                  data-testid={on ? 'focus-rail-pending' : `focus-rail-pending-${lvl}`}
                />
              ) : null}
            </>
          )
          return (
            <Fragment key={lvl}>
              {i > 0 ? <span className={SEP} aria-hidden /> : null}
              {on ? (
                <span ref={currentRef} className={cls} title={title} aria-current="true">
                  {inner}
                </span>
              ) : to ? (
                <Link to={to} className={cls} title={title}>
                  {inner}
                </Link>
              ) : (
                <span className={cls} title={title} aria-disabled={lock ? 'true' : undefined}>
                  {inner}
                </span>
              )}
            </Fragment>
          )
        })}
        <span className={SEP} aria-hidden />
        <Chevron glyph="›" title={zoomOutTitle} to={spec.zoomOut?.to} />
        {/* The moving indicator: one bar, spring-eased, so a zoom reads as a
            move along the ladder rather than a new screen. */}
        <span
          aria-hidden
          data-testid="focus-rail-indicator"
          className="pointer-events-none absolute bottom-[-4px] h-[3px] rounded-full bg-iflow-500"
          style={{
            left: bar?.left ?? 0,
            width: bar?.width ?? 0,
            opacity: bar ? 1 : 0,
            transitionProperty: reduceMotion ? 'none' : 'left, width',
            transitionDuration: reduceMotion ? '0ms' : '400ms',
            transitionTimingFunction: reduceMotion ? undefined : SPRING,
          }}
        />
      </nav>
      <div className="pl-2 text-[10.5px] leading-none text-slate-500" data-testid="youre-at">
        You&apos;re at: <b className="font-semibold text-iflow-800">{SPINE_LEVEL_LABEL[spec.level]}</b>
        {tail.map((part, i) => (
          <span key={i}>
            {' · '}
            <Tail part={part} />
          </span>
        ))}
      </div>
    </div>
  )
}
